import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import TopBarUserNotifications from './TopBarUserNotifications';
import MainMenu from './MainMenu';
import CustomSelect from './CustomSelect';
import { UserContext } from '../App';
import { MainContext } from './MainLayoutWrapper';
import { MethodischDidactischContext } from './MethodischDidactischLayoutWrapper';

function TopBar({ settings, showBackground = false }) {
    const userContext = useContext(UserContext);
    const mainContext = useContext(MainContext)
    const methodischDidactischContext = useContext(MethodischDidactischContext);

    const navigate = useNavigate();
    const location = useLocation();

    const [notifications, setNotifications] = useState([]);
    const [showMainMenu, setShowMainMenu] = useState(false);

    const userSelectedAFilter = mainContext ? !!mainContext.activeFilters.klas || !!mainContext.activeFilters.vak : false;
    const isOnVakcurHome = location.pathname === '/vakcurriculum' || location.pathname === '/vakcurriculum/';
    const isOnVakcur = location.pathname.startsWith('/vakcurriculum');
    const isOnVakcurKlas = location.pathname.startsWith('/vakcurriculum/klas');
    const isOnVakcurBasisvaardigheden = location.pathname.startsWith('/vakcurriculum/basisvaardigheden');
    const isOnBasisvaardighedenCard = location.pathname.startsWith('/vakcurriculum/basisvaardigheden/kaart');
    const isOnMenskunde = location.pathname.startsWith('/menskunde');
    const isOnVsPedagogiek = location.pathname.startsWith('/vs-pedagogiek');
    const isOnMethodischDidactisch = location.pathname.startsWith('/methodisch-didactisch');
    const isOnMethodischDidactischHome = location.pathname === '/methodisch-didactisch' || location.pathname === '/methodisch-didactisch/';
    const isOnOntwikkeling = location.pathname.startsWith('/ontwikkelingsmoment');

    const markNotificationAsRead = useCallback(notificationId => {
        try {
            fetch(settings.apiBaseUrl + 'mark-notification-as-read', {
                method: 'POST',
                headers: userContext.fetchHeaders,
                body: JSON.stringify({ notificationId: notificationId })
            })
                .then(result => result.json())
                .then(response => {
                    if (response.success) setNotifications(response.notifications);
                    else console.error('Error updating notification as read', response);
                })
                ;
        } catch (error) {
            console.error('Error updating notification as read', error);
        }
    }, [userContext.fetchHeaders, settings.apiBaseUrl]);

    const updateNotifications = useCallback(() => {
        if (!userContext.fetchHeaders) return;

        try {
            fetch(settings.apiBaseUrl + 'get-my-notifications', {
                method: 'POST',
                headers: userContext.fetchHeaders,
            })
                .then(result => result.json())
                .then(response => {
                    if (response.success) {
                        setNotifications(response.notifications);

                        const userData = JSON.parse(localStorage.getItem('lpb_user'));
                        localStorage.setItem('lpb_user', JSON.stringify({ ...userData, permissions: response.user_permissions }));
                    }
                    else console.error('Error requesting notifications', response);
                });
        } catch (error) {
            console.error('Error requesting notifications', error);
        }
    }, [userContext.fetchHeaders, settings.apiBaseUrl]);

    function clickHomeBtn() {
        if (location.pathname !== '/vakcurriculum') {
            if (mainContext) return mainContext.helperFunctions.nav('/vakcurriculum')
            return navigate('/vakcurriculum');
        }
    }

    function clickMenskundeBtn() {
        if (mainContext) return mainContext.helperFunctions.nav('/menskunde')
        return navigate('/menskunde');
    }

    function clickVsPedagogiekBtn() {
        if (mainContext) return mainContext.helperFunctions.nav('/vs-pedagogiek')
        return navigate('/vs-pedagogiek');
    }

    function clickMethodischDidactischBtn() {
        if (typeof methodischDidactischContext !== 'undefined' && methodischDidactischContext !== null) {
            methodischDidactischContext.setActiveKlas(null);
            methodischDidactischContext.setActiveMotivatie(null);
            methodischDidactischContext.setActiveVerwerking(null);
            methodischDidactischContext.setActiveVak(null);
        }

        if (isOnMethodischDidactisch) return navigate('/methodisch-didactisch');
        if (mainContext) return mainContext.helperFunctions.nav('/methodisch-didactisch')
        return navigate('/methodisch-didactisch');
    }

    function clickPrikbordBtn() {
        if (mainContext) return mainContext.helperFunctions.nav('/prikbord')
        return navigate('/prikbord');
    }

    function clickOntwikkelingBtn() {
        if (mainContext) return mainContext.helperFunctions.nav('/ontwikkelingsmoment')
        return navigate('/ontwikkelingsmoment');
    }

    function clickBasisvaardighedenBtn() {
        mainContext.helperFunctions.setShowBasisvaardighedenSelectModal(true);
    }

    function clickBasisvaardighedenHideBtn() {
        return navigate('/vakcurriculum/klas/' + mainContext.activeFilters.klas);
    }

    function navigateToView() {
        // When only a klas and no vak is selected
        if (!!mainContext.activeFilters.klas && !mainContext.activeFilters.vak) {
            return mainContext.helperFunctions.nav(`/vakcurriculum/klas/${mainContext.activeFilters.klas}`);
        }

        // When only a vak and no klas is selected
        else if (!!mainContext.activeFilters.vak) {
            return mainContext.helperFunctions.nav(`/vakcurriculum/vak/${mainContext.activeFilters.vak}`);
        }

        // Need to select more filters first
        else {
            alert('Selecteer eerst een klas en/of vak om door te gaan.');
        }
    }

    useEffect(() => {
        updateNotifications();

        // Update the notifications every 30 seconds
        const interval = setInterval(updateNotifications, 30 * 1000);
        return () => clearInterval(interval);
    }, [updateNotifications]);

    // Build the option arrays
    const klasOptions = mainContext ? mainContext.klassen.map(klas => { return { title: klas.human_name, slug: klas.slug } }) : null;
    if (mainContext) klasOptions.unshift({ title: 'Alle klassen', slug: null });

    const basisvaardighedenOptions = (mainContext && Array.isArray(mainContext.mainBasisvaardighedenComplete))
        ? mainContext.mainBasisvaardighedenComplete.map(basisvaardigheid => { return { title: basisvaardigheid.name, slug: basisvaardigheid.slug } })
        : [];

    const basisvaardighedenComplete = mainContext ? mainContext.mainBasisvaardighedenComplete : null;
    const selectedBasisvaardigheidSlug = mainContext ? mainContext.mainBasisvaardigheid : null;
    const activeKlasSlug = mainContext ? mainContext.activeFilters.klas : null;
    const selectedDeelvaardigheidSlug = mainContext ? mainContext.mainDeelvaardigheid : null;

    const deelvaardighedenOptions = useMemo(() => {
        if (!Array.isArray(basisvaardighedenComplete)) return [];

        const basisvaardigheidList = selectedBasisvaardigheidSlug
            ? basisvaardighedenComplete.filter(bv => bv.slug === selectedBasisvaardigheidSlug)
            : basisvaardighedenComplete;

        const slugToTitle = new Map();
        basisvaardigheidList.forEach(bv => {
            (bv.cards || [])
                .filter(card => {
                    if (!card.klassen) return true;
                    if (!activeKlasSlug) return true;
                    return card.klassen.includes(activeKlasSlug);
                })
                .forEach(card => {
                const slugs = Array.isArray(card.deelvaardigheden) ? card.deelvaardigheden : [];
                const titles = Array.isArray(card.deelvaardigheden_format) ? card.deelvaardigheden_format : [];
                slugs.forEach((slug, idx) => {
                    const title = titles[idx] || slug;
                    if (!slugToTitle.has(slug)) slugToTitle.set(slug, title);
                });
            });
        });

        const options = Array.from(slugToTitle.entries()).map(([slug, title]) => ({ title, slug }));
        options.sort((a, b) => a.title.localeCompare(b.title, 'nl', { sensitivity: 'base' }));
        options.unshift({ title: 'Alle deelvaardigheden', slug: null });
        return options;
    }, [basisvaardighedenComplete, selectedBasisvaardigheidSlug, activeKlasSlug]);

    const subdomeinenOptions = useMemo(() => {
        if (!Array.isArray(basisvaardighedenComplete)) return [];

        const basisvaardigheidList = selectedBasisvaardigheidSlug
            ? basisvaardighedenComplete.filter(bv => bv.slug === selectedBasisvaardigheidSlug)
            : basisvaardighedenComplete;

        const slugToTitle = new Map();
        basisvaardigheidList.forEach(bv => {
            (bv.cards || [])
                .filter(card => {
                    if (!card.klassen) return true;
                    if (!activeKlasSlug) return true;
                    return card.klassen.includes(activeKlasSlug);
                })
                .forEach(card => {
                    if (selectedDeelvaardigheidSlug !== null && typeof selectedDeelvaardigheidSlug !== 'undefined') {
                        if (!Array.isArray(card.deelvaardigheden) || !card.deelvaardigheden.includes(selectedDeelvaardigheidSlug)) return;
                    }
                    const slugs = Array.isArray(card.subdomeinen) ? card.subdomeinen : [];
                    const titles = Array.isArray(card.subdomeinen_format) ? card.subdomeinen_format : [];
                    slugs.forEach((slug, idx) => {
                        const title = titles[idx] || slug;
                        if (!slugToTitle.has(slug)) slugToTitle.set(slug, title);
                    });
                });
        });

        const options = Array.from(slugToTitle.entries()).map(([slug, title]) => ({ title, slug }));
        options.sort((a, b) => a.title.localeCompare(b.title, 'nl', { sensitivity: 'base' }));
        options.unshift({ title: 'Alle subdomeinen', slug: null });
        return options;
    }, [basisvaardighedenComplete, selectedBasisvaardigheidSlug, activeKlasSlug, selectedDeelvaardigheidSlug]);

    useEffect(() => {
        if (!mainContext) return;
        if (!deelvaardighedenOptions.length) return;

        const selectedSlug = mainContext.mainDeelvaardigheid;
        if (selectedSlug === null) return; // 'Alle deelvaardigheden' selected

        const exists = deelvaardighedenOptions.some(opt => opt.slug === selectedSlug);
        if (!exists) mainContext.helperFunctions.setMainDeelvaardigheid(null);
    }, [mainContext, deelvaardighedenOptions]);

    useEffect(() => {
        if (!mainContext) return;
        if (!subdomeinenOptions.length) return;

        const selectedSlug = mainContext.mainSubdomein;
        if (selectedSlug === null) return; // 'Alle subdomeinen' selected

        const exists = subdomeinenOptions.some(opt => opt.slug === selectedSlug);
        if (!exists) mainContext.helperFunctions.setMainSubdomein(null);
    }, [mainContext, subdomeinenOptions]);

    const vakOptions = mainContext ? mainContext.vakken.map(vak => { return { title: vak.name, slug: vak.slug } }) : null;
    if (mainContext) vakOptions.unshift({ title: 'Alle vakken', slug: null });

    const trajectOptions = mainContext ? mainContext.trajecten.map(traject => { return { title: traject.name, slug: traject.slug } }) : null;
    if (mainContext) trajectOptions.unshift({ title: 'Alle trajecten', slug: null });

    if (!userContext.isLoggedIn) return null;

    return (<>
        <nav className={'top-bar' + (showBackground ? ' top-bar--bg' : '')}>
            <div className="top-bar__inner">
                <ul className="top-bar__areas">
                    <li><button className={'btn' + (isOnVakcur ? ' btn--mustard' : ' btn--mustard-light')} onClick={clickHomeBtn}>Vakcurriculum</button></li>
                    <li><button className={'btn' + (isOnMenskunde ? ' btn--coral' : ' btn--coral-light')} onClick={clickMenskundeBtn}>Menskunde</button></li>
                    <li><button className={'btn' + (isOnVsPedagogiek ? ' btn--blue' : ' btn--blue-light')} onClick={clickVsPedagogiekBtn}>VS-Pedagogiek</button></li>
                    <li><button className={'btn' + (isOnMethodischDidactisch ? ' btn--purple' : ' btn--mustard-light')} onClick={clickMethodischDidactischBtn}>Methodisch-didactisch</button></li>
                    <li><button className={'btn' + (isOnOntwikkeling ? ' btn--green' : ' btn--green-light')} onClick={clickOntwikkelingBtn}>Ontwikkelingsmoment</button></li>
                </ul>

                <div className="top-bar__right">
                    <div className="top-bar__search-wrapper">
                        <button type="button" className="top-bar__search-btn" onClick={() => mainContext.helperFunctions.setShowSearchOverlay(true)}>Zoeken</button>
                    </div>

                    <TopBarUserNotifications notifications={notifications} markNotificationAsRead={markNotificationAsRead} />

                    <button type="button" className="top-bar__my-profile-btn" onClick={() => navigate('/mijn-account')}>
                        <div style={{ backgroundImage: `url(${userContext.getCurrentUser().avatar})` }}></div>
                    </button>

                    <button type="button" onClick={clickPrikbordBtn} className={'btn' + (location.pathname.startsWith('/prikbord') ? ' btn--active btn--no-interaction' : '')}>Prikbord</button>

                    <button type="button" className="top-bar__🍔" onClick={() => setShowMainMenu(true)}>Menu</button>
                </div>
            </div>

            {(isOnVakcur && !!mainContext && !!mainContext.klassen.length && !isOnVakcurBasisvaardigheden) && <>
                <div className="top-bar__inner top-bar__inner--second">
                    <ul className="top-bar__areas">
                        {!!mainContext &&
                            <ul className={'top-bar__expanding-filters show'}>
                                {!!mainContext.klassen.length && <>
                                    <li><CustomSelect placeholderText="Klas" selectedOptionSlug={mainContext.activeFilters.klas} setSelectedOptionSlug={mainContext.helperFunctions.setKlasFilter} options={klasOptions} /></li>
                                    <li><CustomSelect placeholderText="Vak" selectedOptionSlug={mainContext.activeFilters.vak} setSelectedOptionSlug={mainContext.helperFunctions.setVakFilter} options={vakOptions} /></li>
                                    <li><CustomSelect placeholderText="Traject" selectedOptionSlug={mainContext.activeFilters.traject} setSelectedOptionSlug={mainContext.helperFunctions.setTrajectFilter} options={trajectOptions} /></li>
                                    {!isOnVakcurHome && <li><CustomSelect placeholderText="Lestype" selectedOptionSlug={mainContext.activeFilters.lestype} setSelectedOptionSlug={mainContext.helperFunctions.setLestypeFilter} options={mainContext.lestypen.map(lestype => { return { title: lestype.name, slug: lestype.slug } })} /></li>}
                                    {isOnVakcurHome && userSelectedAFilter && <li><button className="custom-select__go-btn arrow-btn" onClick={navigateToView}>Ga!</button></li>}
                                    {(isOnVakcurKlas && mainContext.activeFilters.klas) && <button className="btn btn--coral" onClick={clickBasisvaardighedenBtn}>Bekijk de basisvaardigheden</button>}
                                </>}
                            </ul>
                        }
                    </ul>
                </div>
            </>}

            {(isOnVakcur && !!mainContext && !!mainContext.klassen.length && isOnVakcurBasisvaardigheden && !isOnBasisvaardighedenCard) && <>
                <div className="top-bar__inner top-bar__inner--second">
                    <ul className="top-bar__areas">
                        {!!mainContext &&
                            <ul className={'top-bar__expanding-filters show'}>
                                {!!mainContext.klassen.length && <>
                                    <li><CustomSelect placeholderText='Klas' selectedOptionSlug={mainContext.activeFilters.klas} setSelectedOptionSlug={mainContext.helperFunctions.setKlasFilter} options={(klasOptions || []).slice(1)} /></li>
                                    <li><CustomSelect placeholderText='Basisvaardigheid' selectedOptionSlug={mainContext.mainBasisvaardigheid} setSelectedOptionSlug={mainContext.helperFunctions.setMainBasisvaardigheid} options={basisvaardighedenOptions || []} /></li>
                                    <li><CustomSelect placeholderText='Deelvaardigheid' selectedOptionSlug={mainContext.mainDeelvaardigheid} setSelectedOptionSlug={mainContext.helperFunctions.setMainDeelvaardigheid} options={deelvaardighedenOptions || []} /></li>
                                    <li><CustomSelect placeholderText='Subdomein' selectedOptionSlug={mainContext.mainSubdomein} setSelectedOptionSlug={mainContext.helperFunctions.setMainSubdomein} options={subdomeinenOptions || []} /></li>

                                    {mainContext.activeFilters.klas && <button className="btn btn--coral" onClick={clickBasisvaardighedenHideBtn}>Terug naar vakcurriculum</button>}
                                </>}
                            </ul>
                        }
                    </ul>
                </div>
            </>}

            {isOnMethodischDidactischHome && methodischDidactischContext.klassen && <>
                <div className="top-bar__inner top-bar__inner--second">
                    <ul className="top-bar__areas">
                        <li><CustomSelect placeholderText="Klas" selectedOptionSlug={methodischDidactischContext.activeKlas} setSelectedOptionSlug={methodischDidactischContext.setActiveKlas} options={methodischDidactischContext.klassen.map(klas => { return { title: klas.human_name, slug: klas.slug } })} /></li>
                        <li><CustomSelect placeholderText="Motivatie" selectedOptionSlug={methodischDidactischContext.activeMotivatie} setSelectedOptionSlug={methodischDidactischContext.setActiveMotivatie} options={methodischDidactischContext.motivaties.map(motivatie => { return { title: motivatie.name, slug: motivatie.slug } })} /></li>
                        <li><CustomSelect placeholderText="Vak" selectedOptionSlug={methodischDidactischContext.activeVak} setSelectedOptionSlug={methodischDidactischContext.setActiveVak} options={methodischDidactischContext.vakken.map(vak => { return { title: vak.name, slug: vak.slug } })} /></li>
                        {/* <li><CustomSelect placeholderText="Verwerking" selectedOptionSlug={methodischDidactischContext.activeVerwerking} setSelectedOptionSlug={methodischDidactischContext.setActiveVerwerking} options={methodischDidactischContext.verwerkingen.map(verwerking => { return { title: verwerking.name, slug: verwerking.slug } })} /></li> */}
                    </ul>
                </div>
            </>}
        </nav>

        <MainMenu showMenu={showMainMenu} setShowMainMenu={setShowMainMenu} />
    </>);
}

export default TopBar;